import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  MessageCircle, 
  Send, 
  Search, 
  Phone, 
  Calendar, 
  User, 
  ExternalLink,
  Copy,
  CheckCircle,
  Clock,
  Home,
  Star,
  Key,
  FileText,
  Settings,
  Edit
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TEMPLATE_ICONS = {
  booking_confirmation: Calendar,
  pre_arrival_reminder: Clock,
  checkin_instructions: Key,
  welcome_arrival: Home,
  checkout_review: Star,
  custom: FileText
};

export default function AdminWhatsApp() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedBookingId = searchParams.get('booking');
  
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [whatsappLinks, setWhatsappLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);

  useEffect(() => {
    fetchBookings();
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = bookings.filter(b => 
        (b.nome_ospite || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (b.codice_prenotazione || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (b.telefono_ospite || '').includes(searchTerm)
      );
      setFilteredBookings(filtered);
    } else {
      setFilteredBookings(bookings);
    }
  }, [searchTerm, bookings]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter bookings with phone numbers and sort by date
      const bookingsWithPhone = response.data
        .filter(b => b.telefono_ospite || b.phone)
        .sort((a, b) => new Date(b.data_arrivo || b.check_in) - new Date(a.data_arrivo || a.check_in));
      setBookings(bookingsWithPhone);
      setFilteredBookings(bookingsWithPhone);
      
      // If there's a preselected booking from URL, select it
      if (preselectedBookingId) {
        const preselected = bookingsWithPhone.find(b => b.id === preselectedBookingId);
        if (preselected) {
          selectBooking(preselected);
        }
      }
    } catch (error) {
      toast.error('Errore caricamento prenotazioni');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/whatsapp/templates?language=it`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchWhatsAppLinks = async (bookingId) => {
    setLoadingLinks(true);
    try {
      const response = await axios.get(`${API}/admin/bookings/${bookingId}/whatsapp-links?language=it`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWhatsappLinks(response.data.links || []);
    } catch (error) {
      toast.error('Errore caricamento link WhatsApp');
    } finally {
      setLoadingLinks(false);
    }
  };

  const selectBooking = (booking) => {
    setSelectedBooking(booking);
    fetchWhatsAppLinks(booking.id);
  };

  const copyToClipboard = async (text, linkKey) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(linkKey);
      toast.success('Link copiato!');
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      toast.error('Errore copia link');
    }
  };

  const openWhatsApp = (link, message) => {
    // If we have the raw message, encode it properly in JavaScript for better emoji support
    if (message) {
      const phone = link.phone || selectedBooking?.telefono_ospite || selectedBooking?.phone || '';
      const cleanPhone = phone.replace(/\D/g, '');
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
    } else {
      window.open(link, '_blank');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed': return 'Confermata';
      case 'pending': return 'In attesa';
      case 'cancelled': return 'Cancellata';
      case 'completed': return 'Completata';
      default: return status;
    }
  };

  return (
    <AdminLayout title="WhatsApp Ospiti">
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-cinzel text-2xl md:text-3xl text-[#1A202C] mb-2 flex items-center gap-3">
              <MessageCircle className="w-8 h-8 text-green-600" />
              WhatsApp Ospiti
            </h1>
            <p className="text-[#4A5568]">
              Invia messaggi WhatsApp agli ospiti usando i template predefiniti
            </p>
          </div>
          <Button 
            onClick={() => navigate('/admin/whatsapp/templates')}
            variant="outline"
            className="flex items-center gap-2 border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10"
          >
            <Edit className="w-4 h-4" />
            Modifica Template
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Bookings List */}
          <Card className="border-[#E2E8F0]">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#C5A059]" />
                Prenotazioni
              </CardTitle>
              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Cerca per nome, codice o telefono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto space-y-2">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Caricamento...</div>
              ) : filteredBookings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nessuna prenotazione con numero di telefono
                </div>
              ) : (
                filteredBookings.map((booking) => (
                  <div
                    key={booking.id}
                    onClick={() => selectBooking(booking)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all hover:border-green-400 ${
                      selectedBooking?.id === booking.id 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-[#C5A059]" />
                          <span className="font-medium text-[#1A202C]">
                            {booking.nome_ospite || booking.guest_name || 'Ospite'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                          <Phone className="w-3 h-3" />
                          {booking.telefono_ospite || booking.phone}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {booking.data_arrivo || booking.check_in} → {booking.data_partenza || booking.check_out}
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                        {getStatusText(booking.status)}
                      </span>
                    </div>
                    {booking.codice_prenotazione && (
                      <div className="mt-2 text-xs text-gray-400">
                        Codice: {booking.codice_prenotazione}
                      </div>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* WhatsApp Templates */}
          <Card className="border-[#E2E8F0]">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Send className="w-5 h-5 text-green-600" />
                {selectedBooking ? (
                  <>
                    Messaggi per {selectedBooking.nome_ospite || 'Ospite'}
                  </>
                ) : (
                  'Seleziona una prenotazione'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedBooking ? (
                <div className="text-center py-12 text-gray-400">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Seleziona una prenotazione dalla lista per vedere i template messaggi</p>
                </div>
              ) : loadingLinks ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  Caricamento template...
                </div>
              ) : (
                <div className="space-y-3">
                  {whatsappLinks.map((link) => {
                    const IconComponent = TEMPLATE_ICONS[link.template_key] || FileText;
                    return (
                      <div
                        key={link.template_key}
                        className="p-4 rounded-lg border border-gray-200 hover:border-green-300 transition-all"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{link.template_icon}</span>
                            <div>
                              <h4 className="font-medium text-[#1A202C]">{link.template_name}</h4>
                              <p className="text-xs text-gray-500">{link.description}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm text-gray-600 max-h-24 overflow-y-auto">
                          {link.message_preview}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => openWhatsApp(link, link.message)}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Apri WhatsApp
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => copyToClipboard(link.message, link.template_key)}
                            className="px-3"
                          >
                            {copiedLink === link.template_key ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Direct WhatsApp Link */}
                  <div className="pt-3 border-t border-gray-200">
                    <Button
                      onClick={() => openWhatsApp(`https://wa.me/${(selectedBooking.telefono_ospite || selectedBooking.phone || '').replace(/\D/g, '')}`)}
                      variant="outline"
                      className="w-full border-green-300 text-green-700 hover:bg-green-50"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Apri Chat Vuota
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
